


































import Vue, { PropOptions } from 'vue';
import SInputText from '../SInputText/SInputText.vue';
import SDatePicker, { DatePickerConfig } from '../SDatePicker/SDatePicker.vue';
import { ValidationRule } from '../../types';
import { date, dateTime } from '../../filters/date';

/**
 * A date input with our custom date picker. The date should be set (and it returned) in ISO format.
 */
export default Vue.extend({
  name: 'SInputDate',
  components: {
    SInputText,
    SDatePicker,
  },

  filters: {
    date,
    dateTime,
  },

  props: {
    clearable: { type: Boolean, default: false },
    hint: { type: String },
    label: { type: String, default: 'Date' },
    name: { type: String },
    placeholder: { type: String },
    required: { type: Boolean, default: false },
    rules: {
      type: Array,
      default: (): ValidationRule[] => [],
    } as PropOptions<ValidationRule[]>,
    value: {
      type: [Array, String],
    },
    counter: { type: Boolean },
    pickerMode: { type: String, default: 'single' } as PropOptions<'single' | 'range'>,
    hideDetails: { type: Boolean, default: false },
    filled: { type: Boolean, default: true },
    dense: { type: Boolean, default: false },
    enableTime: { type: Boolean, default: false },
    timeFormat: { type: String, default: '24hr' } as PropOptions<'12hr' | '24hr'>,
    disabled: { type: Boolean, default: false },
    minDate: { type: String, default: undefined },
    maxDate: { type: String, default: undefined },
  },

  data(): any {
    return {
      inputElement: null,
      /**
       * Clearing an input with the 'clear' icon/button focuses the input and opens the picker.
       * This allows us to stop it opening and prevents a bug where the input kept getting
       * re-populated, making it impossible to clear.
       */
      justBeenCleared: false,
      /**
       * The inputReady boolean is used to allow us to wait for this component to be mounted
       * so we can then pass an element ref to the SDatepicker component to use as a container
       */
      inputReady: false,
    };
  },

  computed: {
    localValue: {
      get(): string[] | string {
        // Defend against range pickers with a default value that's falsey, should be an empty array
        if (this.pickerMode === 'range' && !this.value) {
          return [];
        }
        return this.value;
      },

      set(val: any): void {
        if (val && val.length > 0) {
          // Emit the date in a different format to what flatpickr gives us
          let newDate;
          if (this.pickerMode === 'range') {
            newDate = val.map((part) => part.toISOString());
          } else {
            newDate = val[0].toISOString();
          }
          this.$emit('input', newDate);
        } else {
          this.$emit('input', '');
        }
      },
    },

    // This value is to tell the vuetify component what to render as the formatted value
    valueStr(): string {
      const { value } = this;

      if (!value) return '';

      const format = this.enableTime
        ? this.$options!.filters.dateTime
        : this.$options!.filters.date;

      if (this.pickerMode === 'range') {
        const formatted = value.map((val) => format(val));
        return formatted.join(' - ');
      }

      return format(value);
    },

    flatPickrInstance(): any {
      return (this.$refs.datePicker as any).datePicker;
    },

    flatPickrConfig(): DatePickerConfig {
      return {
        dateFormat: 'Z',
        mode: this.pickerMode,
        defaultDate: this.localValue,
        enableTime: this.enableTime,
        time_24hr: !!(this.timeFormat === '24hr'),
        minDate: this.minDate,
        maxDate: this.maxDate,
      };
    },
  },

  mounted() {
    this.inputReady = true;
  },

  beforeDestroy() {
    this.handleClear();
  },

  methods: {
    openPicker(): void {
      if (!this.justBeenCleared && !(this.flatPickrInstance as any).isOpen) {
        (this.flatPickrInstance as any).open();
      }
    },
    handleClear(): void {
      this.localValue = '';
      this.justBeenCleared = true;
      setTimeout(() => { this.justBeenCleared = false; }, 0);
    },
  },
});
